import React, { useLayoutEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { useMediaQuery } from "@mui/material";
import { getAppVersion } from "../redux/app/action";
import { useDispatch } from "react-redux";
import {
  UseAuth,
  UseIsAdmin,
  UseUserDetails,
  UseAccess,
  UseIsDemo,
  UseIsPrime,
} from "../hooks/auth";
import {
  UseCompanyLoading,
  UseSubscriptionLevel,
  UseCompanyUsersRefresh,
} from "../hooks/company";
import { getCompany } from "../redux/company/actions";

import ProtectedRoute from "./ProtectedRoute";
import routes from "./routes";
import AppInstaller from "../AppInstaller";

import socket from "../configs/socket";
import socketConstants from "../constants/socket";

import MainDashboard from "../pages/Main/Main";
import UnauthorizedPage from "../pages/UnauthorizedPage";


const { SOCKET_EVENT } = socketConstants;

function AppRoutes() {
  const currentUser = UseUserDetails();
  const auth = UseAuth();
  const access = UseAccess();
  const isDemo = UseIsDemo();
  const isPrime = UseIsPrime();
  const isAdmin = UseIsAdmin();
  const isMobileView = useMediaQuery("(max-width:767px)");
  // const subscriptionLevel = UseSubscriptionLevel();
  const availableRoutes = isPrime
    ? [...routes]
    : routes.filter((route) => !route.isPrime);
  const publicRoutes = availableRoutes.filter((route) => route.isPublic);
  const authRoutes = availableRoutes.filter((route) => !route.isPublic);
  const dispatch = useDispatch();

  // Retrieve new data for every app load / refresh
  UseCompanyUsersRefresh();

  React.useEffect(() => {
    if (auth) {
      dispatch(getCompany());
    }
  }, [auth, dispatch]);

  // KPI scores are fetched by useKpiData() mounted in the Main shell, which re-fetches
  // when companyId / workspace become available after persist rehydration.

  React.useEffect(() => {
    const userId = currentUser?._id;
    if (!auth || !userId) {
      if (socket.connected) socket.disconnect();
      return undefined;
    }

    if (!socket.connected) socket.connect();

    const register = () => {
      socket.emit(SOCKET_EVENT.NEW_USER_CONNECTED, currentUser);
    };

    register();
    socket.on('connect', register);
    return () => socket.off('connect', register);
  }, [auth, currentUser]);

  React.useEffect(() => {
    dispatch(getAppVersion());
  }, [dispatch]);

  // Stop the auto scroll to top while the page still rendering
  const { scrollX, scrollY } = window;
  useLayoutEffect(() => {
    window.scrollTo(scrollX, scrollY);
  });

  return (
    <>
      {auth && <AppInstaller />}
      <BrowserRouter>
        <Routes>
          {publicRoutes.map(({ key, path, element: Element }) => (
            <Route key={key} path={path} element={<Element />} />
          ))}
          <Route
            element={
              <ProtectedRoute isAllowed={!!auth} redirectPath={`/login?url=${window.location.pathname}${window.location.search}`} />
            }
          >
            <Route path="/" element={<MainDashboard />}>
              {authRoutes.map(
                ({
                  key,
                  path,
                  element: Element,
                  isAdmin: adminOnlyPage,
                  isDemo: demoPage,
                  isFreemium: freemiumPage = false,
                  desktopOnly,
                }) => {
                  const forAdmin = !isAdmin && adminOnlyPage; // Restricted pages to admin only
                  const forMobile = !!desktopOnly && isMobileView; // Restricted pages to desktop only
                  const accessRequired =
                    (access && access[key] === false) // || // Restricted access for subscription level
                    // (!freemiumPage && subscriptionLevel === 0) || // Restricted access for freemium users
                    // (!demoPage && isDemo); // Restrict access for demo users to demo pages only

                  if (accessRequired || forAdmin || forMobile) {
                    return (
                      <Route
                        key={`${key}-unauthorized`}
                        path={path}
                        element={
                          <UnauthorizedPage
                            upgradeRequired={false}
                            forAdmin={forAdmin}
                          />
                        }
                      />
                    );
                  }
                  return <Route key={key} path={path} element={<Element />} />;
                },
              )}
            </Route>
          </Route>
          <Route
            path="/healthcheck"
            element={<h3>Hey There!!! The App is Healthy</h3>}
          />
        </Routes>
      </BrowserRouter>

      <Toaster
        position="bottom-center"
        containerStyle={{
          // Clear the floating mobile bottom-nav dock (14px off-screen +
          // ~66px tall, plus the home-indicator safe area) instead of
          // sitting behind/under it. Harmless extra headroom on desktop,
          // where that dock doesn't exist.
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
        }}
        toastOptions={{
          duration: 3500,
          style: {
            background: "#0B1320",
            color: "#fff",
            fontFamily: '"DM Sans", "Inter", "Arial", sans-serif',
            fontSize: "13.5px",
            fontWeight: 600,
            borderRadius: "12px",
            padding: "12px 16px",
            boxShadow: "0 1px 0 rgba(255,255,255,0.08) inset, 0 4px 10px rgba(15,23,42,0.15), 0 20px 40px -14px rgba(15,23,42,0.35)",
            maxWidth: "92vw",
          },
          success: {
            iconTheme: { primary: "#34D399", secondary: "#0B1320" },
          },
          error: {
            iconTheme: { primary: "#F87171", secondary: "#0B1320" },
          },
        }}
      />
    </>
  );
}

export default AppRoutes;
